import { Component, OnInit, ChangeDetectionStrategy, WritableSignal, signal } from '@angular/core'
import { ValidatorFn, Validators } from '@angular/forms'
import { FieldTree, form, min as alaraja } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { firstValueFrom } from 'rxjs'

import { LemonaidViestinTyyppi, LemonaidViestiToiminto } from '@lemon/core-firebase/model/lemonaid-viesti'
import {
  LemonaidViestitHistoriarivi,
  LemonaidViestitJulkaistuListaus,
  LemonaidViestitLataaPyynto,
  LemonaidViestitLataaVastaus,
  LemonaidViestitPoistaJulkaisuPyynto,
  LemonaidViestitTallennaPyynto,
  LemonaidViestitTallennaVastaus,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/lemonaid-viestit'
import {
  SovelluksenFlutterVersiotTallennaPyynto,
  SovelluksenFlutterVersiotTallennaVastaus,
  SovelluksenVersioLataaVastaus,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/sovelluksen-versio'

import { FirebaseLemonator } from '../../_angular/service/firebase-lemonator.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'

interface ViestiValilehti {
  tyyppi: LemonaidViestinTyyppi
  otsikko: string
  kuvaus: string
  viestiKentta: FieldTree<string>
  julkaistu: WritableSignal<LemonaidViestitJulkaistuListaus | null>
  historia: WritableSignal<LemonaidViestitHistoriarivi[]>
  ladataan: WritableSignal<boolean>
  tallennetaan: WritableSignal<boolean>
}

interface SovellusversioidenTila {
  webVersio: WritableSignal<string | null>
  minKentta: FieldTree<number | null>
  availableKentta: FieldTree<number | null>
  ladataan: WritableSignal<boolean>
  tallennetaan: WritableSignal<boolean>
}

interface Versiorajat {
  min: number
  max: number
}

function annaRajojenValidaattori(rajat: Versiorajat | null): ValidatorFn {
  return rajat ? Validators.compose([Validators.min(rajat.min), Validators.max(rajat.max)]) : Validators.nullValidator
}

@Component({
  templateUrl: './lemonaid-viestit.component.html',
  styleUrls: ['./lemonaid-viestit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LemonaidViestitComponent implements OnInit {
  valilehdet: ViestiValilehti[] = [
    {
      tyyppi: 'kirjautunut',
      otsikko: 'Kirjautuneille käyttäjille',
      kuvaus: 'Viesti näytetään Lemonaidin yläosassa kaikille kirjautuneille käyttäjille.',
      viestiKentta: form(signal('')),
      julkaistu: signal(null),
      historia: signal([]),
      ladataan: signal(true),
      tallennetaan: signal(false),
    },
    {
      tyyppi: 'julkinen',
      otsikko: 'Kirjautumattomille käyttäjille',
      kuvaus: 'Viesti näytetään sivuilla, jotka näkyvät asiakkaille ennen sisäänkirjautumista.',
      viestiKentta: form(signal('')),
      julkaistu: signal(null),
      historia: signal([]),
      ladataan: signal(true),
      tallennetaan: signal(false),
    },
  ]
  valittuValilehti = 0

  readonly minRajat = signal<Versiorajat | null>(null)
  readonly availableRajat = signal<Versiorajat | null>(null)

  sovellusversiot: SovellusversioidenTila = {
    webVersio: signal(null),
    minKentta: form(signal<number | null>(null), polku => {
      alaraja(polku, 0)
      validoiReaktiivisesti(polku, kontrolli => annaRajojenValidaattori(this.minRajat())(kontrolli))
    }),
    availableKentta: form(signal<number | null>(null), polku => {
      alaraja(polku, 0)
      validoiReaktiivisesti(polku, kontrolli => annaRajojenValidaattori(this.availableRajat())(kontrolli))
    }),
    ladataan: signal(true),
    tallennetaan: signal(false),
  }

  private readonly SUURIN_SALLITTU_VERSIOMUUTOS = 10

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _dialog: MatDialog,
  ) {}

  ngOnInit() {
    for (const valilehti of this.valilehdet) {
      this._lataa(valilehti)
    }
    this._lataaSovellusversiot()
  }

  annaToiminnonNimi(toiminto: LemonaidViestiToiminto): string {
    if (toiminto === 'julkaisu') {
      return 'Julkaistu'
    }
    if (toiminto === 'julkaisun-poisto') {
      return 'Julkaisu poistettu'
    }
    return 'Vedos tallennettu'
  }

  private async _lataa(valilehti: ViestiValilehti) {
    valilehti.ladataan.set(true)
    try {
      const pyynto: LemonaidViestitLataaPyynto = { tyyppi: valilehti.tyyppi }
      const vastaus = await this._firebaseLemonator.functionsCall<LemonaidViestitLataaPyynto, LemonaidViestitLataaVastaus>('lemonaidViestitLataa', pyynto)
      if (vastaus.e) {
        this._snackbar.open('Viestin lataus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      valilehti.julkaistu.set(vastaus.julkaistu)
      valilehti.historia.set(vastaus.historia)
      asetaArvo(valilehti.viestiKentta, vastaus.historia[0]?.viesti ?? vastaus.julkaistu?.viesti ?? '')
    } catch (err) {
      console.error('Viestin lataus epäonnistui', err)
      this._snackbar.open('Viestin lataus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      valilehti.ladataan.set(false)
    }
  }

  async tallennaVedos(valilehti: ViestiValilehti) {
    valilehti.tallennetaan.set(true)
    try {
      const pyynto: LemonaidViestitTallennaPyynto = { tyyppi: valilehti.tyyppi, viesti: valilehti.viestiKentta().value() ?? '' }
      const vastaus = await this._firebaseLemonator.functionsCall<LemonaidViestitTallennaPyynto, LemonaidViestitTallennaVastaus>('lemonaidViestitTallennaVedos', pyynto)
      if (vastaus.e) {
        this._snackbar.open('Vedoksen tallennus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Vedos tallennettu', 'Sulje', { duration: 3000 })
      await this._lataa(valilehti)
    } catch (err) {
      console.error('Vedoksen tallennus epäonnistui', err)
      this._snackbar.open('Vedoksen tallennus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      valilehti.tallennetaan.set(false)
    }
  }

  async julkaise(valilehti: ViestiValilehti) {
    const viesti = valilehti.viestiKentta().value()?.trim()
    if (!viesti) {
      this._snackbar.open('Viesti ei voi olla tyhjä', 'Sulje', { duration: 4000 })
      return
    }

    const data: AreYouSureDialogData = {
      header: valilehti.julkaistu() ? 'Julkaise viesti uudelleen' : 'Julkaise viesti',
      text: `Viesti tulee heti näkyviin ${valilehti.tyyppi === 'julkinen' ? 'kaikille, myös kirjautumattomille käyttäjille' : 'kaikille kirjautuneille käyttäjille'}. Julkaistaanko?`,
      rightAction: 'Julkaise',
    }
    const varmistettu = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!varmistettu) {
      return
    }

    valilehti.tallennetaan.set(true)
    try {
      const pyynto: LemonaidViestitTallennaPyynto = { tyyppi: valilehti.tyyppi, viesti: viesti }
      const vastaus = await this._firebaseLemonator.functionsCall<LemonaidViestitTallennaPyynto, LemonaidViestitTallennaVastaus>('lemonaidViestitJulkaise', pyynto)
      if (vastaus.e) {
        this._snackbar.open('Julkaisu epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Viesti julkaistu', 'Sulje', { duration: 3000 })
      await this._lataa(valilehti)
    } catch (err) {
      console.error('Julkaisu epäonnistui', err)
      this._snackbar.open('Julkaisu epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      valilehti.tallennetaan.set(false)
    }
  }

  private async _lataaSovellusversiot() {
    this.sovellusversiot.ladataan.set(true)
    try {
      const vastaus = await this._firebaseLemonator.functionsCall<void, SovelluksenVersioLataaVastaus>('sovelluksenVersioLataa', null)
      if (vastaus.e) {
        this._snackbar.open('Sovellusversioiden lataus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this.sovellusversiot.webVersio.set(vastaus.versio)
      asetaArvo(this.sovellusversiot.minKentta, vastaus.flutterCellPhoneMin ?? null)
      asetaArvo(this.sovellusversiot.availableKentta, vastaus.flutterCellPhoneAvailable ?? null)
      this.minRajat.set({
        min: vastaus.flutterCellPhoneMin ? vastaus.flutterCellPhoneMin - this.SUURIN_SALLITTU_VERSIOMUUTOS : 1,
        max: vastaus.flutterCellPhoneMin ? vastaus.flutterCellPhoneMin + this.SUURIN_SALLITTU_VERSIOMUUTOS : 999,
      })
      this.availableRajat.set({
        min: vastaus.flutterCellPhoneAvailable ? vastaus.flutterCellPhoneAvailable - this.SUURIN_SALLITTU_VERSIOMUUTOS : 1,
        max: vastaus.flutterCellPhoneAvailable ? vastaus.flutterCellPhoneAvailable + this.SUURIN_SALLITTU_VERSIOMUUTOS : 999,
      })
    } catch (err) {
      console.error('Sovellusversioiden lataus epäonnistui', err)
      this._snackbar.open('Sovellusversioiden lataus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.sovellusversiot.ladataan.set(false)
    }
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  // Negatiivisella arvolla kentän min-attribuutin nollaraja syrjäyttää ladatun alarajan virheviestissä kuten ennenkin
  annaAlaraja(kentta: FieldTree<number | null>, rajat: Versiorajat | null): number | undefined {
    return kentta().value() < 0 ? 0 : rajat?.min
  }

  private _annaBuildNumero(arvo: number | null): number | null | 'virheellinen' {
    if (arvo === null || arvo === undefined || (arvo as unknown) === '') {
      return null
    }
    if (!Number.isInteger(arvo) || arvo < 0) {
      return 'virheellinen'
    }
    return arvo
  }

  async tallennaSovellusversiot() {
    if (!this.sovellusversiot.minKentta().valid() || !this.sovellusversiot.availableKentta().valid()) {
      return
    }
    const min = this._annaBuildNumero(this.sovellusversiot.minKentta().value())
    const available = this._annaBuildNumero(this.sovellusversiot.availableKentta().value())
    if (min === 'virheellinen' || available === 'virheellinen') {
      this._snackbar.open('Build-numeroiden pitää olla ei-negatiivisia kokonaislukuja', 'Sulje', { duration: 4000 })
      return
    }
    if (min !== null && available !== null && min > available) {
      this._snackbar.open('Pienin sallittu versio ei voi olla suurempi kuin viimeisin saatavilla oleva', 'Sulje', { duration: 4000 })
      return
    }

    const data: AreYouSureDialogData = {
      header: 'Tallenna sovellusversiot',
      text: 'Pienimmän sallitun version nostaminen estää sitä vanhempien sovellusversioiden käytön välittömästi. Tallennetaanko?',
      rightAction: 'Tallenna',
    }
    const varmistettu = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!varmistettu) {
      return
    }

    this.sovellusversiot.tallennetaan.set(true)
    try {
      const pyynto: SovelluksenFlutterVersiotTallennaPyynto = { flutterCellPhoneMin: min, flutterCellPhoneAvailable: available }
      const vastaus = await this._firebaseLemonator.functionsCall<SovelluksenFlutterVersiotTallennaPyynto, SovelluksenFlutterVersiotTallennaVastaus>('sovelluksenVersioTallennaFlutterVersiot', pyynto)
      if (vastaus.e) {
        this._snackbar.open('Sovellusversioiden tallennus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Sovellusversiot tallennettu', 'Sulje', { duration: 3000 })
      await this._lataaSovellusversiot()
    } catch (err) {
      console.error('Sovellusversioiden tallennus epäonnistui', err)
      this._snackbar.open('Sovellusversioiden tallennus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.sovellusversiot.tallennetaan.set(false)
    }
  }

  async poistaJulkaisu(valilehti: ViestiValilehti) {
    const data: AreYouSureDialogData = {
      header: 'Poista julkaisu',
      text: 'Viesti poistuu heti näkyvistä. Poistetaanko julkaisu?',
      rightAction: 'Poista julkaisu',
    }
    const varmistettu = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!varmistettu) {
      return
    }

    valilehti.tallennetaan.set(true)
    try {
      const pyynto: LemonaidViestitPoistaJulkaisuPyynto = { tyyppi: valilehti.tyyppi }
      const vastaus = await this._firebaseLemonator.functionsCall<LemonaidViestitPoistaJulkaisuPyynto, LemonaidViestitTallennaVastaus>('lemonaidViestitPoistaJulkaisu', pyynto)
      if (vastaus.e) {
        this._snackbar.open('Julkaisun poisto epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Julkaisu poistettu', 'Sulje', { duration: 3000 })
      await this._lataa(valilehti)
    } catch (err) {
      console.error('Julkaisun poisto epäonnistui', err)
      this._snackbar.open('Julkaisun poisto epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      valilehti.tallennetaan.set(false)
    }
  }
}
